import { useRef, useState, useEffect, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { useScrollReveal } from "@/hooks/use-gsap";
import { Link } from "wouter";
import { WEBSITE_ROUTES } from "@/lib/config";
import { useLanguage } from "@/components/language-provider";
import { LazyVideo } from "@/components/lazy-video";
import clsx from "clsx";

gsap.registerPlugin(ScrollTrigger);

// ========================================
// ANIMATED TITLE
// ========================================
export function AnimatedTitle({ title, containerClass, alternateColors = false }: { title: string; containerClass?: string; alternateColors?: boolean }) {
  const containerRef = useRef<HTMLDivElement>(null);
  let wordCounter = 0;

  useGSAP(() => {
    const isMobile = window.innerWidth < 768;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: { trigger: containerRef.current, start: "100 bottom", end: "center bottom", toggleActions: "play none none reverse" },
      });
      tl.to(".animated-word", {
        opacity: 1, transform: "translate3d(0, 0, 0) rotateY(0deg) rotateX(0deg)",
        ease: isMobile ? "power2.out" : "power2.inOut", stagger: isMobile ? 0.01 : 0.02,
      }, 0);
    }, containerRef);
    return () => ctx.revert();
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className={clsx("flex flex-col gap-1 text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl uppercase leading-[.85] font-black tracking-tight", containerClass)}>
      {title.split("<br />").map((line, index) => (
        <div key={index} className="flex flex-wrap justify-center gap-2.5 px-2 sm:gap-3 sm:px-4 md:gap-4">
          {line.split(" ").map((word, idx) => {
            const isGreen = alternateColors && wordCounter % 2 === 1;
            wordCounter++;
            return <span key={idx} className={clsx("animated-word", isGreen && "text-accent-green")} dangerouslySetInnerHTML={{ __html: word }} />;
          })}
        </div>
      ))}
    </div>
  );
}

// ========================================
// ANIMATED SECTION
// ========================================
export function AnimatedSection({
  children, className = "", delay = 0, direction = "up",
}: { children: React.ReactNode; className?: string; delay?: number; direction?: "up" | "left" | "right" | "scale"; }) {
  const ref = useScrollReveal({
    y: direction === "up" ? 40 : 0, x: direction === "left" ? -40 : direction === "right" ? 40 : 0,
    scale: direction === "scale" ? 0.9 : 1, delay,
  });
  return <div ref={ref} className={className}>{children}</div>;
}

// ========================================
// BENTO TILT — 3D hover (desktop only)
// ========================================
export function BentoTilt({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const [transformStyle, setTransformStyle] = useState("");
  const itemRef = useRef<HTMLDivElement>(null);
  const [isLowPerf, setIsLowPerf] = useState(false);

  useEffect(() => {
    // Disable tilt on touch devices OR low-end hardware (≤4 cores or <4GB RAM)
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    const lowCores = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
    const lowMem = (navigator as any).deviceMemory && (navigator as any).deviceMemory < 4;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setIsLowPerf(isTouch || lowCores || lowMem || reduceMotion);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isLowPerf || !itemRef.current) return;
    const { left, top, width, height } = itemRef.current.getBoundingClientRect();
    const tiltX = ((e.clientY - top) / height - 0.5) * 10;
    const tiltY = ((e.clientX - left) / width - 0.5) * -10;
    setTransformStyle(`perspective(700px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(.97, .97, .97)`);
  }, [isLowPerf]);

  return (
    <div ref={itemRef} className={clsx("bento-tilt", className)} onMouseMove={handleMouseMove} onMouseLeave={() => setTransformStyle("")} style={{ transform: transformStyle, transition: "transform 0.3s ease-out" }}>
      {children}
    </div>
  );
}

// ========================================
// BENTO CARD — hover plays video once, then shows poster
// ========================================
export function BentoCard({
  src, poster, hoverPlay, title, description, isComingSoon, gradient, linkTo,
}: {
  src?: string; poster?: string; hoverPlay?: boolean; title: React.ReactNode; description?: string; isComingSoon?: boolean; gradient?: string; linkTo?: string;
}) {
  const { language } = useLanguage();
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoverOpacity, setHoverOpacity] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const btnRef = useRef<HTMLDivElement>(null);

  // Reset when language changes
  useEffect(() => { setHasPlayed(false); setIsPlaying(false); }, [src]);

  // Hover on the CARD triggers video play
  const handleCardEnter = () => {
    if (hoverPlay && !hasPlayed) setIsPlaying(true);
  };

  // Video ended — show poster, mark as played
  const handleVideoEnded = () => { setIsPlaying(false); setHasPlayed(true); };

  const handleBtnMove = (e: React.MouseEvent) => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const btnLabel = language === "pt" ? "saiba mais" : "learn more";
  const targetRoute = linkTo || WEBSITE_ROUTES.enterpriseContact;

  return (
    <div
      className="relative size-full overflow-hidden rounded-2xl"
      style={{ background: gradient || "linear-gradient(135deg, #2a4a2a 0%, #2a2a4a 100%)" }}
      onMouseEnter={handleCardEnter}
    >
      {src && (
        <LazyVideo
          src={src}
          poster={poster}
          playing={hoverPlay ? isPlaying : undefined}
          onEnded={hoverPlay ? handleVideoEnded : undefined}
          autoLoop={!hoverPlay}
          className="absolute inset-0"
        />
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20 z-[1]" />

      <div className="relative z-10 flex size-full flex-col justify-between p-4 sm:p-5 md:p-6 text-white">
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-black uppercase tracking-tight drop-shadow-lg">{title}</h1>
          {description && <p className="mt-2 max-w-72 text-xs sm:text-sm md:text-base text-white/90 drop-shadow-md leading-relaxed">{description}</p>}
        </div>

        {isComingSoon && (
          <Link href={targetRoute}>
            <div ref={btnRef} onMouseMove={handleBtnMove} onMouseEnter={() => setHoverOpacity(1)} onMouseLeave={() => setHoverOpacity(0)}
              className="relative flex w-fit cursor-pointer items-center gap-1 overflow-hidden rounded-full border border-white/30 bg-white/10 px-4 py-2 sm:px-5 sm:py-2.5 text-xs uppercase text-white backdrop-blur-sm hover:bg-white/20 transition-colors min-h-[44px]">
              <div className="pointer-events-none absolute -inset-px transition duration-300 hidden md:block" style={{ opacity: hoverOpacity, background: `radial-gradient(100px circle at ${cursorPos.x}px ${cursorPos.y}px, rgba(125,179,66,0.5), transparent)` }} />
              <span className="relative z-20 font-medium">{btnLabel}</span>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}

// ========================================
// VIDEO PREVIEW — parallax (desktop only)
// ========================================
export function VideoPreview({ children }: { children: React.ReactNode }) {
  const [isHovering, setIsHovering] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback(({ clientX, clientY, currentTarget }: React.MouseEvent) => {
    const rect = currentTarget.getBoundingClientRect();
    const xOff = clientX - (rect.left + rect.width / 2);
    const yOff = clientY - (rect.top + rect.height / 2);
    if (isHovering) {
      gsap.to(sectionRef.current, { x: xOff, y: yOff, rotationY: xOff / 2, rotationX: -yOff / 2, transformPerspective: 500, duration: 1, ease: "power1.out" });
      gsap.to(contentRef.current, { x: -xOff, y: -yOff, duration: 1, ease: "power1.out" });
    }
  }, [isHovering]);

  useEffect(() => {
    if (!isHovering) {
      gsap.to(sectionRef.current, { x: 0, y: 0, rotationY: 0, rotationX: 0, duration: 1, ease: "power1.out" });
      gsap.to(contentRef.current, { x: 0, y: 0, duration: 1, ease: "power1.out" });
    }
  }, [isHovering]);

  return (
    <section ref={sectionRef} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} className="absolute z-50 size-full overflow-hidden rounded-lg" style={{ perspective: "500px" }}>
      <div ref={contentRef} className="origin-center rounded-lg" style={{ transformStyle: "preserve-3d" }}>{children}</div>
    </section>
  );
}
